/**
 * Canonical section registry — defines all built-in report sections.
 * Used as the default section order when no template is active.
 */
import type { ReportTypeId } from './reportTypes';
import {
  gridLedStandbyPlanningReportHasContent,
  type GridLedStandbyPlanningReportInput,
} from '../lib/generationImpact/gridLedStandbyPlanningReportContent';

export interface SectionDef {
  sectionId: string;
  defaultLabel: string;
  page: number;
  supportsColumnLayout: boolean;
  /**
   * STAGE 5.1 — per-TYPE optionality override.
   *
   * `OPTIONAL_SEMANTIC_SECTION_IDS` is keyed by `sectionId` alone, so it cannot
   * express "required for power-availability, optional for feasibility" — and
   * that is exactly what absorbing the grid-screening half needs. Section
   * arrays are already per-type, so the override lives here.
   *
   * Set it when a section's evidence is genuinely conditional FOR THIS TYPE.
   * The contract then carries `omissionPolicy: 'record-explicit-omission'`, so
   * an absent section is recorded in the report-v2 document's `omissions[]` as
   * `required: false` rather than blocking the export — the same shape
   * `blindSpots` and `generationScenarioComparison` already use.
   *
   * CORRECTION (2026-09-04): an earlier revision of this comment claimed "the
   * omission stays visible to the scorer (`sourceFloor` reads
   * `qaFindings.sectionOmissions`), so this weakens no verdict". That was
   * WRONG, and the mistake is worth naming because it is easy to repeat: there
   * are TWO disjoint omission channels. `document.omissions[]` is the one this
   * flag feeds. `qaFindings.sectionOmissions` — the one `sourceFloor` reads
   * (sourceFloor.ts) — is stamped by `computeSectionOmissions`, which
   * early-returns `[]` for every report type except `public-sentiment`
   * (sectionOmissions.ts). So marking a section optional here does NOT surface
   * anything to the composite scorer on any other type.
   *
   * The conclusion still holds, but for a different reason: it weakens no
   * verdict because the scorer never saw these sections in the first place.
   * Do not cite `sourceFloor` as the safety net for this flag.
   */
  evidenceOptional?: boolean;
}

export type ReportSectionGroup = 'overview' | 'analysis' | 'risks-actions' | 'evidence';
export type ReportSectionSemanticRole =
  | 'narrative'
  | 'kpi'
  | 'table'
  | 'chart'
  | 'map'
  | 'decision'
  | 'appendix'
  | 'legal';

/** Presentation and projection policy frozen into the semantic revision. */
export interface ReportSectionContract extends SectionDef {
  group: ReportSectionGroup;
  semanticRole: ReportSectionSemanticRole;
  required: boolean;
  surfaceEligibility: Readonly<{
    privateView: boolean;
    publicView: boolean;
    pdf: boolean;
    docx: boolean;
    pptx: boolean;
    csv: boolean;
  }>;
  preferredSpan: 'flow' | 'full-page-width';
  splittable: boolean;
  keepRules: Readonly<{
    headingWithFollowingLines: number;
    captionWithFigure: boolean;
    repeatTableHeader: boolean;
    keepRowsIntact: boolean;
  }>;
  continuationLabel: string;
  expectedExhibits: readonly ('kpi' | 'table' | 'chart' | 'map' | 'references')[];
  omissionPolicy: 'block-if-missing' | 'record-explicit-omission';
}

const OPTIONAL_SEMANTIC_SECTION_IDS = new Set([
  'toc',
  'appendix',
  'blindSpots',
  'radarChart',
  'lcoeChart',
  'pathway_comparison',
  'generationLayoutExhibit',
  'generationScenarioComparison',
  'sentimentResearchDetail',
  'sentimentAppendix',
]);

function semanticRoleForSection(sectionId: string): ReportSectionSemanticRole {
  if (sectionId === 'gridLedStandbyPlanning') return 'table';
  const id = sectionId.toLowerCase();
  if (/legal|notice|disclaimer/.test(id)) return 'legal';
  if (/appendix|source|blind|evidence|reference/.test(id)) return 'appendix';
  if (/map|parcel|routing|placement|topography|layout.*exhibit/.test(id)) return 'map';
  if (/chart|profile|sensitivity|montecarlo/.test(id)) return 'chart';
  if (/matrix|comparison|breakdown|stream|ranking|inventory/.test(id)) return 'table';
  if (/decision|conclusion|executive|path.?to.?go/.test(id)) return 'decision';
  if (/summary|overview|capacity|index|score/.test(id)) return 'kpi';
  return 'narrative';
}

function groupForSection(sectionId: string): ReportSectionGroup {
  const id = sectionId.toLowerCase();
  if (/appendix|source|blind|evidence|reference|legal/.test(id)) return 'evidence';
  if (/risk|action|mitigation|conclusion|decision|path.?to.?go/.test(id)) return 'risks-actions';
  if (/executive|summary|intent|context|overview/.test(id)) return 'overview';
  return 'analysis';
}

/**
 * Enrich the long-lived ordering registry with deterministic presentation
 * policy. This is the only section-policy constructor used by document,
 * viewer, pagination and projection code; no surface may infer its own order.
 */
export function toReportSectionContract(section: SectionDef): ReportSectionContract {
  const semanticRole = semanticRoleForSection(section.sectionId);
  const required = !OPTIONAL_SEMANTIC_SECTION_IDS.has(section.sectionId)
    && section.evidenceOptional !== true;
  const exhibitRole = semanticRole === 'kpi'
    || semanticRole === 'table'
    || semanticRole === 'chart'
    || semanticRole === 'map'
    || semanticRole === 'appendix';
  const expectedExhibits: ReportSectionContract['expectedExhibits'] = semanticRole === 'appendix'
    ? ['references']
    : semanticRole === 'kpi' || semanticRole === 'table' || semanticRole === 'chart' || semanticRole === 'map'
      ? [semanticRole]
      : [];
  return {
    ...section,
    group: groupForSection(section.sectionId),
    semanticRole,
    required,
    surfaceEligibility: {
      privateView: true,
      publicView: true,
      pdf: true,
      docx: true,
      pptx: section.sectionId !== 'toc',
      csv: section.sectionId !== 'toc' && semanticRole !== 'legal',
    },
    preferredSpan: !section.supportsColumnLayout || exhibitRole ? 'full-page-width' : 'flow',
    splittable: semanticRole === 'narrative' || semanticRole === 'appendix' || semanticRole === 'legal',
    keepRules: {
      headingWithFollowingLines: 3,
      captionWithFigure: true,
      repeatTableHeader: true,
      keepRowsIntact: true,
    },
    continuationLabel: `${section.defaultLabel} (continued)`,
    expectedExhibits,
    omissionPolicy: required ? 'block-if-missing' : 'record-explicit-omission',
  };
}

/** Retired section ids — stripped from templates and report order at runtime. */
export const RETIRED_SECTION_IDS = new Set<string>(['verdict', 'categoryScoresTable', 'btmScatter', 'end', 'keyFindings']);

/** Report types that no longer carry the feasibility-style extendedData tail. */
export const STRIP_EXTENDED_DATA_REPORT_TYPES = new Set<ReportTypeId>([
  'custom-brief',
  'executive-summary',
]);

/** Per-type section id remaps applied during template normalization. */
export const TYPE_SECTION_ID_MIGRATIONS: Partial<Record<ReportTypeId, Record<string, string>>> = {
  'public-sentiment': { extendedData: 'sentimentResearchDetail' },
};

/** Retired section ids remapped to their successor at template normalization time. */
export const SECTION_ID_MIGRATIONS: Record<string, string> = {
  keyFindings: 'projectIntentOverview',
};

export function migrateSectionIdForType(id: string, reportType?: ReportTypeId | null): string {
  const typeMigrated = reportType
    ? TYPE_SECTION_ID_MIGRATIONS[reportType]?.[id] ?? id
    : id;
  return SECTION_ID_MIGRATIONS[typeMigrated] ?? typeMigrated;
}

export function stripRetiredSectionIds(ids: readonly string[]): string[] {
  return ids.filter((id) => !RETIRED_SECTION_IDS.has(id));
}

/** Studio-controllable Table of Contents block — injected after executive summary. */
export const TOC_SECTION_DEF: SectionDef = {
  sectionId: 'toc',
  defaultLabel: 'Table of Contents',
  page: 1,
  // RRQ — the Contents reads best as a SINGLE-COLUMN, full-width list. With
  // `supportsColumnLayout: true` a two-column preset (Aurora/Quarterly/…) split
  // the TOC into two columns (the user flagged this). Setting it false makes
  // `resolveSectionPageColumnCountForSection('toc')` return 1 unconditionally
  // (registry guard) so the TOC is always one column, and classifies it as a
  // full-width `span` block in the page flow — matching the documented intent
  // (SiteRagReportView "TOC classify as data-section-flow='span'").
  supportsColumnLayout: false,
};

/** RV6 — self-honesty block; studio-controllable, defaults after appendix. */
export const BLIND_SPOTS_SECTION_DEF: SectionDef = {
  sectionId: 'blindSpots',
  defaultLabel: 'What this report does not know',
  page: 3,
  // Self-honesty prose / bullet list — flows in two columns under a
  // two-column theme.
  supportsColumnLayout: true,
};

/** @deprecated Retired — end-of-report stamp removed; theme footer closes the report. */
export const END_SECTION_DEF: SectionDef = {
  sectionId: 'end',
  defaultLabel: 'End of report',
  page: 3,
  supportsColumnLayout: false,
};

/**
 * Shared closing sections — extended site data, conclusion, references appendix.
 *
 * These are narrative / list-style closers (extended-data prose, conclusion
 * prose, reference appendix). They flow in two columns under a two-column
 * theme; any wide tables they contain span both columns via the
 * `column-span: all` rule, so no data is lost.
 */
// COMBINES (fold #5) — Extended Site Data is FOLDED INTO the Appendix &
// References section for every STANDARD_REPORT_TAIL consumer: the def is
// dropped from the tail (order: conclusion → appendix), so the standalone
// numbered section + its TOC entry disappear, and the content renders as an
// appendix SUBSECTION instead (ExtendedDataSection `renderAsAppendixSubsection`
// slotted into ReferencesSection on view/print; the DOCX/PPTX exports emit the
// same fold inside their appendix flows). The def itself is KEPT exported —
// archived templates / heading-override editors still reference the id + label,
// and public-sentiment's variant (`sentimentResearchDetail`, its own SectionDef
// below) remains a STANDALONE section. An ARCHIVED sectionOrder that still
// lists `extendedData` is filtered at resolution time (projectTemplateSectionIds
// drops non-canonical ids), so it cannot double-render alongside the fold.
export const EXTENDED_DATA_SECTION: SectionDef = {
  sectionId: 'extendedData',
  defaultLabel: 'Extended Site Data',
  page: 3,
  supportsColumnLayout: true,
};

/**
 * COMBINES (fold #5) — true for report types whose Extended Site Data renders
 * as a SUBSECTION inside the Appendix & References section (every type that
 * carried the feasibility-style `extendedData` tail). Public-sentiment keeps
 * its own standalone `sentimentResearchDetail` section, and the STRIP types
 * (custom-brief / executive-summary) never carried the tail at all. Shared by
 * all four surfaces (view/print via SiteRagReportView, DOCX, PPTX) so the fold
 * decision can never diverge per surface.
 */
export function reportTypeFoldsExtendedDataIntoAppendix(
  reportType: ReportTypeId | undefined | null,
): boolean {
  const t = reportType ?? 'feasibility';
  if (t === 'public-sentiment') return false;
  return !STRIP_EXTENDED_DATA_REPORT_TYPES.has(t);
}

/** PSR — sentiment scan + community research (replaces extendedData on public-sentiment). */
export const SENTIMENT_RESEARCH_DETAIL_SECTION: SectionDef = {
  sectionId: 'sentimentResearchDetail',
  defaultLabel: 'Sentiment Research Detail',
  page: 3,
  supportsColumnLayout: true,
};

/**
 * PSR P3 — the sentiment source-grouped evidence inventory gets its OWN section
 * id (`sentimentAppendix`) so it no longer COLLIDES with the standard references
 * `appendix`. Before P3 the router remapped the `appendix` id to
 * SentimentAppendixSection for public-sentiment, which hijacked the references /
 * methodology appendix slot (rendered inline by ReferencesSection under the same
 * `data-section="appendix"`): the two appendices fought over one id (duplicate
 * anchor / TOC entry). Splitting the sentiment inventory to its own id lets BOTH
 * the references appendix AND the sentiment evidence inventory render. Renders
 * via NEW_SECTION_REGISTRY (SentimentAppendixSection reads report.sentimentAppendix).
 */
export const SENTIMENT_APPENDIX_SECTION: SectionDef = {
  sectionId: 'sentimentAppendix',
  defaultLabel: 'Sentiment Evidence Inventory',
  page: 3,
  supportsColumnLayout: true,
};

export const CONCLUSION_SECTION: SectionDef = {
  sectionId: 'conclusion',
  defaultLabel: 'Conclusion & Verdict',
  page: 3,
  // Running prose closer — flows in two columns under a two-column theme.
  supportsColumnLayout: true,
};

export const APPENDIX_SECTION: SectionDef = {
  sectionId: 'appendix',
  defaultLabel: 'Appendix & References',
  page: 3,
  // Reference / methodology lists — narrative-style, flows in two columns.
  supportsColumnLayout: true,
};

// v1.2.2 — the Conclusion is the reader's decision close; the appendix carries
// the supporting reference material.
// COMBINES (fold #5) — EXTENDED_DATA_SECTION was REMOVED from this tail
// (2026-07-03, William): Extended Site Data now renders INSIDE the Appendix &
// References section as a subsection (see the fold comment on
// EXTENDED_DATA_SECTION above). Order: conclusion → appendix. TOC + numbering
// derive from this registry, so the standalone "Extended Site Data" entry
// disappears from every consumer automatically.
export const STANDARD_REPORT_TAIL: SectionDef[] = [
  CONCLUSION_SECTION,
  APPENDIX_SECTION,
];

/** Insert `toc` immediately after `executiveSummary` when absent. */
export function withTocSection(sections: SectionDef[]): SectionDef[] {
  if (sections.some(s => s.sectionId === 'toc')) return sections;
  const execIdx = sections.findIndex(
    s => s.sectionId === 'executiveSummary' || s.sectionId === 'executive',
  );
  const insertAt = execIdx >= 0 ? execIdx + 1 : 0;
  const next = [...sections];
  next.splice(insertAt, 0, TOC_SECTION_DEF);
  return next;
}

/** Insert `blindSpots` immediately after `appendix`, or after `conclusion` / at end. */
export function withBlindSpotsSection(sections: SectionDef[]): SectionDef[] {
  if (sections.some(s => s.sectionId === 'blindSpots')) return sections;
  const appendixIdx = sections.findIndex(s => s.sectionId === 'appendix');
  let insertAt = sections.length;
  if (appendixIdx >= 0) insertAt = appendixIdx + 1;
  else {
    const conclusionIdx = sections.findIndex(s => s.sectionId === 'conclusion');
    if (conclusionIdx >= 0) insertAt = conclusionIdx + 1;
  }
  const next = [...sections];
  next.splice(insertAt, 0, BLIND_SPOTS_SECTION_DEF);
  return next;
}

/** Insert `end` immediately after `blindSpots`, or after `appendix` / at end. */
export function withEndSection(sections: SectionDef[]): SectionDef[] {
  if (sections.some(s => s.sectionId === 'end')) return sections;
  const blindIdx = sections.findIndex(s => s.sectionId === 'blindSpots');
  let insertAt = sections.length;
  if (blindIdx >= 0) insertAt = blindIdx + 1;
  else {
    const appendixIdx = sections.findIndex(s => s.sectionId === 'appendix');
    if (appendixIdx >= 0) insertAt = appendixIdx + 1;
    else {
      const conclusionIdx = sections.findIndex(s => s.sectionId === 'conclusion');
      if (conclusionIdx >= 0) insertAt = conclusionIdx + 1;
    }
  }
  const next = [...sections];
  next.splice(insertAt, 0, END_SECTION_DEF);
  return next;
}

export const REPORT_SECTIONS: SectionDef[] = [
  { sectionId: 'executiveSummary', defaultLabel: 'Executive Summary', page: 1, supportsColumnLayout: true },
  { sectionId: 'projectIntentOverview', defaultLabel: 'Project Intent & Site Overview', page: 1, supportsColumnLayout: false },
  { sectionId: 'btmOverview', defaultLabel: 'Behind-the-Meter Technology', page: 1, supportsColumnLayout: true },
  { sectionId: 'workloadPowerFit', defaultLabel: 'Workload & Power-Source Fit', page: 1, supportsColumnLayout: true },
  // COMBINES (fold #3) — the firm-capacity (ELCC) deep-dive is FOLDED INTO the
  // Grid Power Study for FEASIBILITY: GridStudySection renders the
  // `capacityCreditDeepDive` (ELCC) content as a sub-block, so the standalone
  // `capacityCreditDeepDive` section def is dropped from the feasibility order
  // (it remains a STANDALONE section in POWER_AVAILABILITY_SECTIONS). The
  // feasibility prompt STILL emits `powerAnalysis.capacityCreditDeepDive`; the
  // merged Grid renderer consumes it. The shared `capacityCreditDeepDive`
  // registry component is feasibility-suppressed in NewTypeSectionsRouter so an
  // ARCHIVED feasibility sectionOrder that still lists the id does not
  // double-render alongside the fold.
  { sectionId: 'gridStudy', defaultLabel: 'Grid Power Study', page: 1, supportsColumnLayout: true },
  // ── STAGE 5.1 — the grid-screening half of Power Availability lands HERE ───
  //
  // BTM means behind-the-meter, and this IS the BTM Feasibility Report. Sending
  // the reader to a sibling report for the power detail was not answering the
  // question this report exists to answer.
  //
  // All six are grounded ENTIRELY in public-provider SED (HIFLD grid, EIA
  // electricity, NREL solar/wind) — no generation scenario, no BOM, no declared
  // load. That is precisely why they can live on the always-available report
  // while the adequacy half (firm capacity, BTM technology, LCOE, grid-vs-BTM)
  // moves behind a promoted-scenario gate: a GRID-ONLY site never promotes a
  // scenario, so if these did NOT land here such a site would lose grid
  // analysis entirely — no queue position, no tariff, no curtailment.
  //
  // Added as STANDALONE defs rather than folded into `gridStudy` (the local
  // convention for `capacityCreditDeepDive` above): the point of the move is
  // that the detail is present and separately addressable, and a fold would
  // bury six analyses inside one heading. `SectionsRouter`'s registry is keyed
  // by sectionId GLOBALLY, so the existing PowerAvailability components mount
  // here with no new renderers.
  //
  // While Stage 5 commit 1 stands alone these ids are ALSO still listed in
  // POWER_AVAILABILITY_SECTIONS. The duplication is deliberate and temporary —
  // it is what makes this commit independently revertable — and commit 2
  // removes them there.
  // STAGE 5.1 — the grid-screening half absorbed from power-availability. All
  // six are `evidenceOptional` FOR FEASIBILITY (power's copies below stay
  // required), for two independent reasons:
  //
  //   1. MIGRATION. `hasCompleteLegacyExportConversion` (draftExportGate.ts:56)
  //      requires EVERY required registry section to carry blocks. Every
  //      feasibility report generated before this commit has zero blocks for
  //      all six, so registering them as required would retroactively make
  //      every legacy feasibility report un-exportable.
  //   2. EVIDENCE. `renewableIntegration` reads solar/wind and `gridRedundancy`
  //      reads onSiteInfrastructure — all OPTIONAL SED for feasibility, and
  //      onSiteInfrastructure has no provider at all. A required section whose
  //      evidence is optional is an unsatisfiable contract.
  //
  // This also keeps the registry honest with the prompt: buildFeasibilityNarrative
  // instructs "OMIT any field the evidence cannot support", so a blocking gate
  // would contradict the instruction the model is given.
  { sectionId: 'gridCapacity', defaultLabel: 'Grid Capacity & Substation Analysis', page: 1, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'interconnectionQueue', defaultLabel: 'Interconnection Queue (LGIA/SGIA)', page: 1, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'rateStructures', defaultLabel: 'Utility Rate Structures', page: 2, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'curtailmentRisk', defaultLabel: 'Curtailment Risk', page: 2, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'renewableIntegration', defaultLabel: 'Renewable Integration & On-Site Potential', page: 2, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'gridRedundancy', defaultLabel: 'Grid Redundancy & Uptime', page: 2, supportsColumnLayout: true, evidenceOptional: true },
  // COMBINES (merge #1) — Logistics & Connectivity. MERGES the former
  // `transportStudy` ("Transport & Logistics") + `connectivityStudy`
  // ("Connectivity & Fiber") into ONE section: the surviving `transportStudy`
  // section (re-labelled "Logistics & Connectivity") renders BOTH sub-bodies
  // (see TransportStudySection). The `connectivityStudy` section def is dropped
  // from the feasibility order; its FIELD (`report.connectivityStudy`) is still
  // emitted by the prompt and consumed by the merged renderer. The
  // `connectivityStudy` registry component is feasibility-suppressed in
  // NewTypeSectionsRouter so an archived feasibility sectionOrder does not
  // double-render it.
  { sectionId: 'transportStudy', defaultLabel: 'Logistics & Connectivity', page: 2, supportsColumnLayout: true },
  // COMBINES (merge #2) — Siting & Severe Weather. MERGES the former
  // `sitingNotes` ("Siting Notes") + `climateSevereWeather` ("Climate & Severe
  // Weather") into ONE section: the surviving `sitingNotes` section
  // (re-labelled "Siting & Severe Weather") renders BOTH bodies inline in
  // SiteRagReportView (siting prose + climate KPI rail / risks / monthly
  // profile chart). The `climateSevereWeather` section def is dropped from the
  // feasibility order; its FIELD (`report.climateSevereWeather`) is still
  // emitted by the prompt and consumed by the merged inline renderer. The
  // standalone `climateSevereWeather` inline mount is feasibility-suppressed so
  // an archived feasibility sectionOrder does not double-render it.
  { sectionId: 'sitingNotes', defaultLabel: 'Siting & Severe Weather', page: 2, supportsColumnLayout: true },
  // ── TWO-STAGE Phase 3 part B (2026-09-07) — the WATER SCREEN absorbed from the
  // retired Water Resource report. Same SHARED section ids and the same
  // `waterAnalysis.*` fields the water report used, so the router
  // (NEW_SECTION_REGISTRY), the DOCX/PPTX emitters, the audit text extractor and
  // WATER-TRUTH-1 read a feasibility water screen exactly as they read the
  // water report. Screening depth only: supply, groundwater & surface water,
  // rights (with the consumptive-use gate), drought — the scenario's water DUTY
  // lives on Generation & Impact (P3.2, `waterDuty`); cooling-by-technology,
  // wastewater, conservation and total cost stay with the retired report.
  // `evidenceOptional`, like the absorbed grid sections: an ungrounded water
  // section stays absent and records its omission — never hollow prose.
  // Labels are VERBATIM the water report's (`getCanonicalLabel` resolves a
  // shared id to one label whichever registry is asked).
  { sectionId: 'waterScreening', defaultLabel: 'Modeled Water Use', page: 2, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'supplyAvailability', defaultLabel: 'Supply Availability & Demand', page: 2, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'groundwater', defaultLabel: 'Groundwater & Surface Water', page: 2, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'waterRights', defaultLabel: 'Water Rights Analysis', page: 2, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'droughtResilience', defaultLabel: 'Drought Resilience (1-in-10 / 1-in-100)', page: 2, supportsColumnLayout: true, evidenceOptional: true },
  // Task 5 — `climateProfile` (the standalone "Climate Profile Chart" section)
  // was retired: its chart is now FOLDED into the Climate & Severe Weather
  // section body (SiteRagReportView), so it no longer needs its own section
  // slot. Removing the def drops it from the default section order + the TOC
  // (deriveTocEntries walks this registry), eliminating the near-empty page the
  // standalone section stranded. Any archived report whose persisted
  // `sectionOrder` still lists `climateProfile` simply finds no renderer (the
  // standalone mount is gone) — harmless, no orphan node.
  { sectionId: 'riskAssessment', defaultLabel: 'Risk Assessment', page: 2, supportsColumnLayout: true },
  { sectionId: 'actionPlan', defaultLabel: 'Action Plan & Pathway', page: 2, supportsColumnLayout: true },
  // DEFECT-A (2026-06-27): flow, not span. The Feasibility-Profile section pairs a
  // radar EXHIBIT (data-export-capture → still column-span:all) with a long
  // ContextualKeyFindingsBlock prose tail. As `span` the WHOLE section is one
  // unfragmentable block → ~2019px → +949px overflow on one page. As `flow` the
  // exhibit still spans while the prose columnises + fragments across pages.
  { sectionId: 'radarChart', defaultLabel: 'Feasibility Profile', page: 3, supportsColumnLayout: true },
  // COMBINES (fold #4) — the pinned pathway comparison is FOLDED INTO the LCOE
  // Cost Comparison for FEASIBILITY: LcoeChartSection renders the
  // `pathway_comparison` content as a sub-block. The standalone
  // `pathway_comparison` section def is dropped from the feasibility order (it
  // remains a STANDALONE section in TEST_FIT / FINANCIAL / EXECUTIVE_SUMMARY).
  // The pinned-comparison data (`report.pathwayComparisonAtGen`) is unchanged;
  // the merged LCOE renderer consumes it (self-omitting when no comparison with
  // 2+ alternatives was pinned). The standalone feasibility inline pathway
  // mount in SiteRagReportView is removed (the fold replaces it).
  { sectionId: 'lcoeChart', defaultLabel: 'LCOE Cost Comparison', page: 3, supportsColumnLayout: false },
  ...STANDARD_REPORT_TAIL,
];

/** Environmental report sections */
export const ENVIRONMENTAL_SECTIONS: SectionDef[] = [
  { sectionId: 'executiveSummary', defaultLabel: 'Executive Summary', page: 1, supportsColumnLayout: false },
  { sectionId: 'projectIntentOverview', defaultLabel: 'Project Intent & Site Overview', page: 1, supportsColumnLayout: false },
  { sectionId: 'nepaScreening', defaultLabel: 'NEPA/CEQA Screening', page: 1, supportsColumnLayout: true },
  { sectionId: 'ecologicalAssessment', defaultLabel: 'Ecological Assessment', page: 1, supportsColumnLayout: true },
  { sectionId: 'waterResources', defaultLabel: 'Water Resources', page: 2, supportsColumnLayout: true },
  { sectionId: 'airQualityEmissions', defaultLabel: 'Air Quality & Emissions', page: 2, supportsColumnLayout: true },
  { sectionId: 'floodNaturalHazards', defaultLabel: 'Flood & Natural Hazards', page: 2, supportsColumnLayout: true },
  { sectionId: 'culturalHistorical', defaultLabel: 'Cultural & Historical Resources', page: 2, supportsColumnLayout: true },
  { sectionId: 'landUseZoning', defaultLabel: 'Land Use & Zoning', page: 2, supportsColumnLayout: true },
  { sectionId: 'noiseVisualImpact', defaultLabel: 'Noise & Visual Impact', page: 2, supportsColumnLayout: true },
  { sectionId: 'mitigationPlan', defaultLabel: 'Mitigation Plan', page: 3, supportsColumnLayout: true },
  // RTU-2 (row 0.99235) — three NEPA-canonical sections placed in the
  // permitting-pathway page neighbourhood because they describe the
  // procedural framework (alternatives, cumulative impacts, public
  // participation) the permits feed into.
  { sectionId: 'alternativesAnalysis', defaultLabel: 'Alternatives Analysis', page: 3, supportsColumnLayout: true },
  { sectionId: 'cumulativeImpacts', defaultLabel: 'Cumulative Impacts', page: 3, supportsColumnLayout: true },
  { sectionId: 'publicParticipationPlan', defaultLabel: 'Public Participation Plan', page: 3, supportsColumnLayout: true },
  { sectionId: 'permittingPathway', defaultLabel: 'Permitting Pathway', page: 3, supportsColumnLayout: true },
  { sectionId: 'environmentalRiskMatrix', defaultLabel: 'Risk Assessment', page: 3, supportsColumnLayout: false },
  { sectionId: 'radarChart', defaultLabel: 'Environmental Profile', page: 3, supportsColumnLayout: true }, // DEFECT-A: flow so the findings-prose tail fragments (see Feasibility Profile above)
  ...STANDARD_REPORT_TAIL,
];

/** Test fit / site layout report sections */
export const TEST_FIT_SECTIONS: SectionDef[] = [
  { sectionId: 'executiveSummary', defaultLabel: 'Executive Summary', page: 1, supportsColumnLayout: false },
  { sectionId: 'projectIntentOverview', defaultLabel: 'Project Intent & Site Overview', page: 1, supportsColumnLayout: false },
  { sectionId: 'parcelOverview', defaultLabel: 'Parcel & Net Buildable Area', page: 1, supportsColumnLayout: true },
  { sectionId: 'topography', defaultLabel: 'Topography & Slopes', page: 1, supportsColumnLayout: true },
  { sectionId: 'buildingPlacement', defaultLabel: 'Optimal Building Placement', page: 2, supportsColumnLayout: true },
  { sectionId: 'setbackCompliance', defaultLabel: 'Setback Compliance', page: 2, supportsColumnLayout: true },
  { sectionId: 'parkingAccess', defaultLabel: 'Parking & Emergency Access', page: 2, supportsColumnLayout: true },
  { sectionId: 'stormwater', defaultLabel: 'Stormwater Detention', page: 2, supportsColumnLayout: true },
  { sectionId: 'utilityRouting', defaultLabel: 'Utility Routing – BTM Plant & DC Feeds', page: 2, supportsColumnLayout: true },
  { sectionId: 'phasedBuildout', defaultLabel: 'Phased Buildout Plan', page: 3, supportsColumnLayout: true },
  { sectionId: 'maxCapacity', defaultLabel: 'Max Campus Capacity – BTM Sizing Ceiling', page: 3, supportsColumnLayout: true },
  { sectionId: 'generationLayoutExhibit', defaultLabel: 'On-Site Generation Test Fit Exhibit', page: 3, supportsColumnLayout: false },
  // The candidate set the promoted scenario was chosen FROM. Optional: the
  // BODY renders only when a real comparison (two or more configurations) was
  // recorded, so a single-scenario report shows no new section on any surface.
  // NOTE — "no new section" is NOT "byte-identical": registering an optional
  // section adds it to the report-v2 document contract, which emits a
  // zero-block section entry plus an explicit `required: false` omission
  // (`omissionPolicy: 'record-explicit-omission'`) — exactly how `blindSpots`
  // already behaves. That moves `documentHash`, so adding an optional section
  // here legitimately re-stales `scripts/ci/fixtures/report-v2-shadow-baseline.v1.json`
  // and the baseline must be re-emitted (see docs/architecture/report-generation-v2-rollout.md).
  { sectionId: 'generationScenarioComparison', defaultLabel: 'Generation Configurations Screened', page: 3, supportsColumnLayout: false },
  // RTU-3 (row 0.99235) — two test-fit-canonical sections placed in the
  // risk-matrix neighbourhood because they characterise the
  // build-deliverability envelope (constructability + cost range) the
  // risk matrix then weights.
  { sectionId: 'constructabilityAssessment', defaultLabel: 'Constructability Assessment', page: 3, supportsColumnLayout: true },
  { sectionId: 'costRangeEstimate', defaultLabel: 'Cost Range Estimate', page: 3, supportsColumnLayout: true },
  { sectionId: 'bop_economics', defaultLabel: 'Economics', page: 3, supportsColumnLayout: false },
  // R-131-FIN-DECISION-K — cites pinned pathway comparison alongside BoP economics.
  { sectionId: 'pathway_comparison', defaultLabel: 'Pathway Comparison', page: 3, supportsColumnLayout: false },
  { sectionId: 'riskMatrix', defaultLabel: 'Risk Assessment', page: 3, supportsColumnLayout: false },
  ...STANDARD_REPORT_TAIL,
];

/** Power availability report sections */
export const POWER_AVAILABILITY_SECTIONS: SectionDef[] = [
  { sectionId: 'executiveSummary', defaultLabel: 'Executive Summary', page: 1, supportsColumnLayout: false },
  { sectionId: 'projectIntentOverview', defaultLabel: 'Project Intent & Site Overview', page: 1, supportsColumnLayout: false },
  { sectionId: 'powerStrategyContext', defaultLabel: 'Power Strategy & Requirements', page: 1, supportsColumnLayout: true },
  // STAGE 5.2 commit 2 (2026-09-05) — the six grid-screening sections
  // (gridCapacity, interconnectionQueue, rateStructures, curtailmentRisk,
  // renewableIntegration, gridRedundancy) now live ONLY on BTM Feasibility
  // (Stage 5.1 absorbed them); the temporary duplication commit 1 kept here
  // for revertability is removed. The renderer sentinel moved with them
  // (`TYPE_SENTINELS['power-availability']`, effectiveSectionOrder.ts).
  // R-FIN-PHASE-D — firm-capacity (ELCC) adequacy + power-basis deep-dive,
  // placed next to gridCapacity because it sizes the grid against the FACILITY
  // load and firm-deliverable capacity (planning-default ELCC).
  { sectionId: 'capacityCreditDeepDive', defaultLabel: 'Capacity-Credit & Firm Adequacy (ELCC)', page: 1, supportsColumnLayout: true },
  // P3.2 — the promoted scenario's water DUTY (gal/day, frozen at generation).
  // Entitlement stays on Water Resource. Optional by evidence: absent until a
  // generation scenario is promoted, recorded as an explicit omission.
  { sectionId: 'waterScreening', defaultLabel: 'Modeled Water Use', page: 2, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'waterDuty', defaultLabel: 'Water Duty (Promoted Scenario)', page: 2, supportsColumnLayout: false, evidenceOptional: true },
  { sectionId: 'btmTechComparison', defaultLabel: 'BTM Technology Deep Dive', page: 2, supportsColumnLayout: true },
  { sectionId: 'workloadPowerFit', defaultLabel: 'Workload & Power-Source Fit', page: 2, supportsColumnLayout: true },
  { sectionId: 'lcoeBreakdown', defaultLabel: 'LCOE Breakdown by Source', page: 3, supportsColumnLayout: false },
  { sectionId: 'gridVsBtm', defaultLabel: 'Grid vs BTM Decision Matrix', page: 3, supportsColumnLayout: false },
  { sectionId: 'powerRiskMatrix', defaultLabel: 'Risk Assessment', page: 3, supportsColumnLayout: false },
  { sectionId: 'actionPlan', defaultLabel: 'Action Plan', page: 3, supportsColumnLayout: true },
  ...STANDARD_REPORT_TAIL,
];

/** Water resource report sections */
export const WATER_RESOURCE_SECTIONS: SectionDef[] = [
  { sectionId: 'executiveSummary', defaultLabel: 'Executive Summary', page: 1, supportsColumnLayout: false },
  { sectionId: 'projectIntentOverview', defaultLabel: 'Project Intent & Site Overview', page: 1, supportsColumnLayout: false },
  { sectionId: 'waterScreening', defaultLabel: 'Modeled Water Use', page: 1, supportsColumnLayout: true, evidenceOptional: true },
  { sectionId: 'supplyAvailability', defaultLabel: 'Supply Availability & Demand', page: 1, supportsColumnLayout: true },
  { sectionId: 'groundwater', defaultLabel: 'Groundwater & Surface Water', page: 1, supportsColumnLayout: true },
  { sectionId: 'waterRights', defaultLabel: 'Water Rights Analysis', page: 2, supportsColumnLayout: true },
  { sectionId: 'droughtResilience', defaultLabel: 'Drought Resilience (1-in-10 / 1-in-100)', page: 2, supportsColumnLayout: true },
  { sectionId: 'coolingByTech', defaultLabel: 'Cooling Water by BTM Technology', page: 2, supportsColumnLayout: true },
  { sectionId: 'wastewaterStormwater', defaultLabel: 'Wastewater & Stormwater', page: 3, supportsColumnLayout: true },
  { sectionId: 'conservation', defaultLabel: 'Conservation Measures', page: 3, supportsColumnLayout: true },
  { sectionId: 'totalWaterCost', defaultLabel: 'Total Water Cost Analysis', page: 3, supportsColumnLayout: true },
  { sectionId: 'waterRiskMatrix', defaultLabel: 'Risk Assessment', page: 3, supportsColumnLayout: false },
  ...STANDARD_REPORT_TAIL,
];

/**
 * PSR-1 (row 0.99290) — Public Sentiment Report sections.
 *
 * Eight-section layout per the Public Sentiment Report pack: executive
 * roll-up, stakeholder map, per-BTM sentiment, theme deep-dives, trend
 * analysis, comparable-site benchmark, mitigation playbook, and appendix.
 *
 * `usesScoring: false` (see ReportPromptConfig wiring) — there is no
 * `verdict` section here because the SENTIMENT INDEX 0-100 emitted by
 * PSR-2 is a narrative verdict the body composes itself, not a category-
 * scored go/no-go. The PSR-1 scaffold keeps the layout to the eight
 * deliverable sections; PSR-2 may add a `verdict` band if the index lifts
 * to a dedicated callout.
 */
export const PUBLIC_SENTIMENT_SECTIONS: SectionDef[] = [
  { sectionId: 'executive',               defaultLabel: 'Executive',                  page: 1, supportsColumnLayout: false },
  { sectionId: 'stakeholderMap',          defaultLabel: 'Stakeholder Map',            page: 1, supportsColumnLayout: false },
  { sectionId: 'perBtmSentiment',         defaultLabel: 'Per-BTM Sentiment',          page: 2, supportsColumnLayout: true },
  { sectionId: 'themeDeepDives',          defaultLabel: 'Theme Deep Dives',           page: 2, supportsColumnLayout: true },
  { sectionId: 'trendAnalysis',           defaultLabel: 'Trend Analysis',             page: 2, supportsColumnLayout: true },
  { sectionId: 'comparableSiteBenchmark', defaultLabel: 'Comparable-Site Benchmark',  page: 3, supportsColumnLayout: false },
  { sectionId: 'mitigationPlaybook',      defaultLabel: 'Mitigation Playbook',        page: 3, supportsColumnLayout: true },
  SENTIMENT_RESEARCH_DETAIL_SECTION,
  // P3 — the sentiment evidence inventory now has its OWN id ahead of the
  // standard references APPENDIX_SECTION, so both render (no `appendix` collision).
  SENTIMENT_APPENDIX_SECTION,
  APPENDIX_SECTION,
];

/** Custom Brief — executive summary + project context + per-question sections. */
export const CUSTOM_BRIEF_SECTIONS: SectionDef[] = [
  { sectionId: 'executiveSummary', defaultLabel: 'Executive Summary', page: 1, supportsColumnLayout: false },
  { sectionId: 'projectContext', defaultLabel: 'Project Context', page: 1, supportsColumnLayout: true },
  { ...CONCLUSION_SECTION, page: 2 },
  { ...APPENDIX_SECTION, page: 2 },
];

/**
 * Executive Summary — the LIVE Go/No-Go Decision Guide.
 *
 * Leads with the deterministic, LLM-FREE decision sections (recomputed live at
 * render — there is NO generate step), mirroring the Financial report's
 * decisionBand-first ordering:
 *   1. execDecisionBand — the authoritative GO / CONDITIONAL / NO-GO band +
 *                         per-source verdict matrix + binding constraints +
 *                         rationale (ExecutiveDecisionGuideSection, distinct id —
 *                         NOT the financial-only 'decisionBand').
 *   2. pathToGo         — the live Path-to-GO diligence gate + ranked steps +
 *                         blockers (PathToGoReportSection).
 *
 * The remaining narrative sections (executiveSummary / projectIntentOverview /
 * btmViability / …) are retained for the optional export-only LLM narrative and
 * degrade gracefully (render empty / withheld) when no narrative was generated.
 */
export const EXECUTIVE_SUMMARY_REPORT_SECTIONS: SectionDef[] = [
  { sectionId: 'execDecisionBand', defaultLabel: 'Go / No-Go Decision', page: 1, supportsColumnLayout: false },
  { sectionId: 'pathToGo', defaultLabel: 'Path to GO', page: 1, supportsColumnLayout: false },
  { sectionId: 'executiveSummary', defaultLabel: 'Executive Summary', page: 1, supportsColumnLayout: false },
  { sectionId: 'projectIntentOverview', defaultLabel: 'Project Intent & Site Overview', page: 1, supportsColumnLayout: false },
  { sectionId: 'btmViability', defaultLabel: 'BTM Energy Path Assessment', page: 1, supportsColumnLayout: true },
  // POLISH-2 (followup to #34) — `capacityCreditDeepDive` (the ELCC firm-capacity
  // deep-dive) is SCOPED to feasibility + power-availability only. It was removed
  // from the Executive Summary Decision Guide per William ("scope to feasibility +
  // power"): the GO/NO-GO band already synthesises power adequacy from the source
  // reports, so the standalone ELCC strip is redundant here. The section component
  // (CapacityCreditDeepDiveSection) is unchanged — it still serves feasibility /
  // power; only this type's config no longer lists the id.
  // R-131-FIN-DECISION-K — cross-report synthesis cites pinned pathway comparison.
  { sectionId: 'pathway_comparison', defaultLabel: 'Pathway Comparison', page: 1, supportsColumnLayout: false },
  { sectionId: 'crossReportSynthesis', defaultLabel: 'Domain Synthesis', page: 2, supportsColumnLayout: true },
  { sectionId: 'criticalRisks', defaultLabel: 'Critical Risks & Blockers', page: 2, supportsColumnLayout: true },
  { sectionId: 'actionPlan', defaultLabel: 'Recommended Next Steps', page: 2, supportsColumnLayout: true },
  { sectionId: 'sourceReports', defaultLabel: 'Source Reports', page: 3, supportsColumnLayout: true },
  CONCLUSION_SECTION,
  APPENDIX_SECTION,
];

/**
 * FIN-1 (wf w7nuyquwd) — Financial Viability Report sections.
 *
 * Eleven-section layout per the synthesized BUILD SPEC. The body is rendered
 * LIVE / READ-ONLY over the deterministic `report.financialAnalysis` stamp
 * (projectFinance.recompute + finDecision/* engines) — there is NO LLM
 * generation step for the body; the optional narrative is export-only.
 *
 * Ordering (decision-first):
 *   1. decisionBand          — Viable/Conditional/Unfinanceable + binding
 *                              constraints + engine-provenance chip (verdict).
 *   2. financialSummary      — LCOE / NPV / IRR-vs-hurdle / payback /
 *                              CAPEX-per-kW / annual MWh headline metrics.
 *   3. capexOpexBreakdown    — CAPEX/OPEX decomposition + per-phase LCOE.
 *   4. cashflowStream        — annual / cumulative cashflow stream.
 *   5. pathway_comparison    — reuse + augment the live pathway compare.
 *   6. monteCarloBands       — P10/P50/P90 NPV bands + P(positive NPV) / pWin.
 *   7. riskAdjustedWacc      — risk-adjusted WACC / discount-rate stack.
 *   8. sensitivity           — tornado + scenario flips + Base/Upside/Downside.
 *   9. taxAndCommercial      — IRA ITC/PTC + PPA structure as adjustments.
 *  10. financialActionPlan   — deterministic action plan (owner + deadline
 *                              tokens) feeding conclusion.recommendation.
 *  11. STANDARD_REPORT_TAIL  — conclusion + appendix (extended site data now
 *                              folds INSIDE the appendix — fold #5); the
 *                              methodology appendix states the two-engine
 *                              LCOE diff + the fixed Monte-Carlo seed.
 */
export const FINANCIAL_REPORT_SECTIONS: SectionDef[] = [
  { sectionId: 'decisionBand', defaultLabel: 'Financial Verdict', page: 1, supportsColumnLayout: false },
  { sectionId: 'financialSummary', defaultLabel: 'Financial Summary', page: 1, supportsColumnLayout: true },
  { sectionId: 'capexOpexBreakdown', defaultLabel: 'CAPEX / OPEX Breakdown', page: 1, supportsColumnLayout: true },
  { sectionId: 'cashflowStream', defaultLabel: 'Cash-Flow Stream', page: 2, supportsColumnLayout: false },
  // POLISH-2 (followup to #34) — `capacityCreditDeepDive` (the ELCC firm-capacity
  // deep-dive) is SCOPED to feasibility + power-availability only. It was removed
  // from the Financial Viability report per William ("scope to feasibility +
  // power"): the financial body already earns LCOE / NPV off the deterministic
  // financialAnalysis stamp, so the standalone ELCC strip is redundant here. The
  // section component (CapacityCreditDeepDiveSection) is unchanged — it still serves
  // feasibility / power; only this type's config no longer lists the id.
  // R-131-FIN-DECISION-K — reuse the pinned pathway-comparison renderer.
  { sectionId: 'pathway_comparison', defaultLabel: 'Pathway Comparison', page: 2, supportsColumnLayout: false },
  { sectionId: 'monteCarloBands', defaultLabel: 'Monte-Carlo Bands (P10/P50/P90)', page: 2, supportsColumnLayout: false },
  { sectionId: 'riskAdjustedWacc', defaultLabel: 'Risk-Adjusted WACC', page: 2, supportsColumnLayout: true },
  { sectionId: 'sensitivity', defaultLabel: 'Sensitivity & Scenarios', page: 3, supportsColumnLayout: false },
  { sectionId: 'taxAndCommercial', defaultLabel: 'Tax Credits & Commercial Terms', page: 3, supportsColumnLayout: true },
  { sectionId: 'financialActionPlan', defaultLabel: 'Financial Action Plan', page: 3, supportsColumnLayout: true },
  ...STANDARD_REPORT_TAIL,
];

/**
 * Supply Chain Risk — procurement, supplier, lead-time, and quote-risk
 * sections aligned with the deterministic SCM report surfaces.
 */
export const SUPPLY_CHAIN_RISK_SECTIONS: SectionDef[] = [
  { sectionId: 'executiveSummary', defaultLabel: 'Executive Summary – Procurement Posture', page: 1, supportsColumnLayout: false },
  { sectionId: 'projectIntentOverview', defaultLabel: 'Project Intent & Site Overview', page: 1, supportsColumnLayout: false },
  { sectionId: 'procurementOverview', defaultLabel: 'Procurement Overview', page: 1, supportsColumnLayout: true },
  { sectionId: 'criticalPathProcurement', defaultLabel: 'Critical Path & Lead Times', page: 1, supportsColumnLayout: true },
  { sectionId: 'supplyChainRisks', defaultLabel: 'Supply Chain Risks', page: 2, supportsColumnLayout: true },
  { sectionId: 'supplierRiskRanking', defaultLabel: 'Supplier Risk Ranking', page: 2, supportsColumnLayout: false },
  { sectionId: 'quoteComparisons', defaultLabel: 'Quote Comparisons', page: 2, supportsColumnLayout: false },
  { sectionId: 'supplyChainRiskMatrix', defaultLabel: 'Risk Assessment', page: 3, supportsColumnLayout: false },
  { sectionId: 'actionPlan', defaultLabel: 'Mitigation & Action Plan', page: 3, supportsColumnLayout: true },
  ...STANDARD_REPORT_TAIL,
];

/** Interconnection / connectivity report sections */
export const INTERCONNECTION_SECTIONS: SectionDef[] = [
  { sectionId: 'executiveSummary', defaultLabel: 'Executive Summary – Connectivity & BTM Posture', page: 1, supportsColumnLayout: false },
  { sectionId: 'projectIntentOverview', defaultLabel: 'Project Intent & Site Overview', page: 1, supportsColumnLayout: false },
  { sectionId: 'fiberRings', defaultLabel: 'Fiber Availability – 5/25/50km Rings', page: 1, supportsColumnLayout: true },
  { sectionId: 'latencyModel', defaultLabel: 'Latency to Cloud Regions', page: 2, supportsColumnLayout: true },
  { sectionId: 'carrierDiversity', defaultLabel: 'Carrier Diversity', page: 2, supportsColumnLayout: true },
  { sectionId: 'ixProximity', defaultLabel: 'Internet Exchange Proximity', page: 2, supportsColumnLayout: true },
  { sectionId: 'submarineCable', defaultLabel: 'Submarine Cable Access', page: 2, supportsColumnLayout: true },
  { sectionId: 'networkResilience', defaultLabel: 'Network Resilience (DDoS, BGP, N+1)', page: 3, supportsColumnLayout: true },
  { sectionId: 'bandwidthCapacity', defaultLabel: 'Bandwidth Capacity', page: 3, supportsColumnLayout: true },
  { sectionId: 'capexOpex', defaultLabel: 'Year-1 CAPEX/OPEX', page: 3, supportsColumnLayout: true },
  { sectionId: 'connectivityRiskMatrix', defaultLabel: 'Risk Assessment', page: 3, supportsColumnLayout: false },
  ...STANDARD_REPORT_TAIL,
];

const SECTIONS_BY_TYPE: Record<ReportTypeId, SectionDef[]> = {
  feasibility: REPORT_SECTIONS,
  environmental: ENVIRONMENTAL_SECTIONS,
  'test-fit': TEST_FIT_SECTIONS,
  'power-availability': POWER_AVAILABILITY_SECTIONS,
  'water-resource': WATER_RESOURCE_SECTIONS,
  interconnection: INTERCONNECTION_SECTIONS,
  'supply-chain-risk': SUPPLY_CHAIN_RISK_SECTIONS,
  'public-sentiment': PUBLIC_SENTIMENT_SECTIONS,
  'custom-brief': CUSTOM_BRIEF_SECTIONS,
  financial: FINANCIAL_REPORT_SECTIONS,
  'executive-summary': EXECUTIVE_SUMMARY_REPORT_SECTIONS,
};

/**
 * Look up the section list for a report type. Falls back to feasibility if the
 * type isn't registered (defensive — every ReportTypeId is currently mapped).
 */
export const GRID_LED_STANDBY_PLANNING_SECTION: SectionDef = {
  sectionId: 'gridLedStandbyPlanning',
  defaultLabel: 'Grid-led power and standby planning',
  page: 2,
  supportsColumnLayout: false,
};

export function getSectionsForType(
  type: ReportTypeId | undefined | null,
  report?: GridLedStandbyPlanningReportInput | null,
): SectionDef[] {
  const base = !type ? REPORT_SECTIONS : (SECTIONS_BY_TYPE[type] ?? REPORT_SECTIONS);
  const sections = withBlindSpotsSection(withTocSection(base));
  if (!gridLedStandbyPlanningReportHasContent(report)) return sections;
  const anchor = sections.findIndex(section => section.sectionId === 'projectIntentOverview' || section.sectionId === 'projectContext');
  const result = [...sections];
  result.splice(anchor >= 0 ? anchor + 1 : 1, 0, GRID_LED_STANDBY_PLANNING_SECTION);
  return result;
}

/** Exhaustive ordered semantic/presentation registry for one report type. */
export function getSectionContractsForType(
  type: ReportTypeId | undefined | null,
  report?: GridLedStandbyPlanningReportInput | null,
): ReportSectionContract[] {
  return getSectionsForType(type, report).map(toReportSectionContract);
}

/** Default section order (all visible, 1 column) — feasibility registry. */
export function getDefaultSectionConfigs(): import('../types/reportTemplate').SectionConfig[] {
  return getDefaultSectionConfigsForType('feasibility');
}

/** Default section configs for any report type (canonical registry order). */
export function getDefaultSectionConfigsForType(
  reportType: ReportTypeId,
): import('../types/reportTemplate').SectionConfig[] {
  return getSectionsForType(reportType).map(s => ({
    sectionId: s.sectionId,
    type: 'builtin' as const,
    visible: true,
    columnLayout: 1 as const,
    page: s.page,
  }));
}

/**
 * Type-aware section label — uses the canonical registry for the given
 * report type. Prefer this in Template Studio when `configuringType` or
 * `sampleType` is known.
 */
export function getSectionLabel(
  sectionId: string,
  reportType?: ReportTypeId | null,
): string {
  if (sectionId === GRID_LED_STANDBY_PLANNING_SECTION.sectionId) return GRID_LED_STANDBY_PLANNING_SECTION.defaultLabel;
  const hit = getSectionsForType(reportType ?? 'feasibility')
    .find(s => s.sectionId === sectionId);
  return hit?.defaultLabel ?? sectionId;
}

/**
 * Look up a section's canonical label across ALL type registries.
 * Returns the first registry match's defaultLabel, falling back to the
 * sectionId itself if no registry contains it. Used by the template editor
 * to label heading override inputs regardless of which report type the
 * template targets.
 *
 * When `reportType` is provided, delegates to `getSectionLabel` so shared
 * ids (e.g. executiveSummary) resolve to the type-specific BTM-framed label.
 */
export function getCanonicalLabel(
  sectionId: string,
  reportType?: ReportTypeId | null,
): string {
  if (sectionId === GRID_LED_STANDBY_PLANNING_SECTION.sectionId) return GRID_LED_STANDBY_PLANNING_SECTION.defaultLabel;
  if (reportType) {
    const typed = getSectionLabel(sectionId, reportType);
    if (typed !== sectionId) return typed;
  }
  const all: SectionDef[][] = [
    REPORT_SECTIONS, ENVIRONMENTAL_SECTIONS, TEST_FIT_SECTIONS,
    POWER_AVAILABILITY_SECTIONS, WATER_RESOURCE_SECTIONS, INTERCONNECTION_SECTIONS,
    SUPPLY_CHAIN_RISK_SECTIONS, PUBLIC_SENTIMENT_SECTIONS, CUSTOM_BRIEF_SECTIONS,
    FINANCIAL_REPORT_SECTIONS, EXECUTIVE_SUMMARY_REPORT_SECTIONS,
    // COMBINES (fold #5) — ARCHIVED defs kept labelable: `extendedData` left
    // every type list (it folds into the appendix), but archived templates /
    // heading-override maps still carry the id, and the override editors label
    // keys through here. Without this fallback an archived
    // `headingOverrides.extendedData` row would label as the raw id.
    [EXTENDED_DATA_SECTION],
  ];
  for (const list of all) {
    const hit = list.find(s => s.sectionId === sectionId);
    if (hit) return hit.defaultLabel;
  }
  return sectionId;
}

/**
 * Resolve a section heading using the configured override chain.
 *
 * Precedence: per-report override > per-template override > defaultLabel.
 * Whitespace-only or empty overrides are treated as missing (the next tier
 * applies). Used by both the in-app render and the DOCX export so the two
 * stay in sync.
 */
export function resolveHeading(
  sectionId: string,
  defaultLabel: string,
  reportOverrides?: Record<string, string>,
  templateOverrides?: Record<string, string>,
): string {
  const r = reportOverrides?.[sectionId]?.trim();
  if (r) return r;
  const t = templateOverrides?.[sectionId]?.trim();
  if (t) return t;
  return defaultLabel;
}
